import { injectable } from 'inversify'

/**
 * Wraps browser localStorage (invariant 8: all browser APIs behind a gateway).
 * Durable on purpose: for preferences (like theme) that should survive across
 * browser sessions, unlike SessionStorageGateway's session-scoped storage.
 *
 * JSON is this medium's wire format, so serialization belongs here — the same
 * way HttpGateway owns parsing the server envelope. Callers hand over values
 * and get values back; nobody outside sees a string.
 *
 * Corrupt data self-heals: anything that won't parse is dropped and reported as
 * absent, so one bad write can't make the app throw on every boot forever.
 * A *parseable* value is still untrusted, so reads deliberately return
 * `unknown`. A generic return type would let a caller turn an assertion into
 * fake runtime validation merely by choosing `T`.
 */
@injectable()
export class LocalStorageGateway {
  getValue(key: string): unknown | null {
    const raw = window.localStorage.getItem(key)
    if (raw === null) return null

    try {
      return JSON.parse(raw) as unknown
    } catch {
      window.localStorage.removeItem(key)
      return null
    }
  }

  /**
   * `false` when the medium refused the bytes — a `QuotaExceededError` from
   * `setItem`, which is otherwise thrown and would crash the caller. This
   * reports what the medium did; it decides nothing about eviction, that is
   * the caller's policy (CI-13). Existing callers ignore the return, which is
   * fine — a preference write that silently fails is no worse than before.
   */
  setValue<T>(key: string, value: T): boolean {
    try {
      window.localStorage.setItem(key, JSON.stringify(value))
      return true
    } catch {
      return false
    }
  }

  remove(key: string): void {
    window.localStorage.removeItem(key)
  }

  /** Every stored key that starts with `prefix` — for prefix-scoped enumeration and pruning. */
  keys(prefix: string): readonly string[] {
    const matches: string[] = []
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i)
      if (key !== null && key.startsWith(prefix)) matches.push(key)
    }
    return matches
  }
}
